import * as React from "react"
import { Popover as PopoverPrimitive } from "@base-ui/react/popover"

import { cn } from "@/lib/utils"

// Popover Base UI (mismo patrón que tooltip.tsx) — a diferencia del Tooltip
// (hover-only), este es CLICK por default: es el primitivo correcto para
// procedencia/edición en tablet, donde no hay hover. No hace falta Provider
// (sin delay-group compartido entre popovers).
function Popover({ ...props }: PopoverPrimitive.Root.Props) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />
}

function PopoverTrigger({ ...props }: PopoverPrimitive.Trigger.Props) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />
}

function PopoverContent({
  className,
  side = "bottom",
  sideOffset = 6,
  align = "start",
  alignOffset = 0,
  collisionAvoidance,
  children,
  ...props
}: PopoverPrimitive.Popup.Props &
  Pick<
    PopoverPrimitive.Positioner.Props,
    "align" | "alignOffset" | "side" | "sideOffset" | "collisionAvoidance"
  >) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        // Opcional (sin él, el default de Base UI): el popover de Filtros lo
        // usa para no saltar al costado cuando no entra y scrollear adentro.
        collisionAvoidance={collisionAvoidance}
        className="isolate z-50"
      >
        <PopoverPrimitive.Popup
          data-slot="popover-content"
          className={cn(
            // text-xs leading-relaxed: el Portal (renderiza fuera del árbol
            // del trigger) corta la herencia tipográfica y sin esto el
            // popover rendía a 16px del body (punto 10). El otro consumidor
            // (chequeos-tabla.tsx) setea sus propios tamaños adentro — no lo
            // afecta, className siempre puede pisar esto si hiciera falta.
            "z-50 w-72 max-w-[calc(100vw-24px)] origin-(--transform-origin) rounded-lg border border-border-subtle bg-surface-card p-3 text-xs leading-relaxed text-foreground shadow-[0_8px_30px_rgba(20,20,22,0.16)] outline-none data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 dark:shadow-[0_10px_36px_rgba(0,0,0,0.5)]",
            className
          )}
          {...props}
        >
          {children}
        </PopoverPrimitive.Popup>
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  )
}

// Cierra el popover desde adentro («Listo» del popover de Filtros,
// components/filtros/filtros-popover.tsx): Base UI cierra con la razón
// `close-press` y devuelve el foco al trigger. Se usa con `render`.
function PopoverClose({ ...props }: PopoverPrimitive.Close.Props) {
  return <PopoverPrimitive.Close data-slot="popover-close" {...props} />
}

// RedeApp R0 (plan_migracion_reui.md §2.0): PopoverHeader, PopoverTitle y
// PopoverDescription copiados del popover.json del registry base-vega (NO
// correr `shadcn add popover`: pisa este archivo).
function PopoverHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="popover-header"
      className={cn("flex flex-col gap-1 text-sm", className)}
      {...props}
    />
  )
}

function PopoverTitle({ className, ...props }: PopoverPrimitive.Title.Props) {
  return (
    <PopoverPrimitive.Title
      data-slot="popover-title"
      className={cn("font-medium", className)}
      {...props}
    />
  )
}

function PopoverDescription({
  className,
  ...props
}: PopoverPrimitive.Description.Props) {
  return (
    <PopoverPrimitive.Description
      data-slot="popover-description"
      className={cn("text-muted-foreground", className)}
      {...props}
    />
  )
}

export {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverClose,
  PopoverHeader,
  PopoverTitle,
  PopoverDescription,
}
